import { Box, CardMedia, styled, Tooltip, Typography, useTheme } from "@mui/material"
import React, { useEffect, useState } from "react"
import { DirectType } from "../services/api/direct"
import AspectRatioBox from "./AspectRatioBox"
import DefaultPoster from "../assets/default-poster.jpg"
import { FormatDate, StylelessLink } from "./Util"
import { HoverOverlay, Overlay } from "./StyledElements"
import { useAuthContext } from "../services/auth"
import { UserRole } from "../services/auth/auth.types"

import { ReactComponent as LockIcon } from "../assets/svg/lock.svg"
import { ReactComponent as KeyIcon } from "../assets/svg/key.svg"
import { ReactComponent as InternetIcon } from "../assets/svg/internet.svg"

export const DirectCardMedia = styled(CardMedia)({
  paddingTop: "141.42%",
})

interface DirectPosterProps {
  direct: DirectType
}
export const DirectPoster = ({ direct }: DirectPosterProps): React.ReactElement => {
  const { role } = useAuthContext()
  const theme = useTheme()
  return (
    <Box mb={2}>
      <StylelessLink to={`/direct?d=${direct.id}`}>
        <HoverOverlay sx={{ display: "flex", flexDirection: "column", height: "fit-content" }}>
          <AspectRatioBox ratio={70 / 99}>
            <DirectPosterImage direct={direct} />
            <Overlay className="overlay" />
          </AspectRatioBox>
          <Box mt={1} sx={{ position: "relative", zIndex: 2 }}>
            <Box display="flex" alignItems="center" minWidth={0}>
              <Typography noWrap variant="h5" fontSize={"1.2rem"} textOverflow="ellipsis" sx={{ minWidth: 0 }}>
                {direct.title}
              </Typography>
              {(role === UserRole.HYRIS || role === UserRole.GEEK) && direct.privacy !== 1 && (
                <Box ml={"4px"} component={"span"} display="inline-flex" alignItems="center" flexShrink={0}>
                  {(role === UserRole.HYRIS || role === UserRole.GEEK) &&
                    (direct.privacy === 0 ? (
                      <Tooltip title={"Direct publique"}>
                        <InternetIcon width={"1rem"} height={"1rem"} fill={theme.palette.soft.main} />
                      </Tooltip>
                    ) : direct.privacy === 2 ? (
                      <Tooltip title={direct.gpaRestricted ? "Direct caché aux GPA" : "Direct privé"}>
                        <KeyIcon width={"1rem"} height={"1rem"} fill={theme.palette.soft.main} />
                      </Tooltip>
                    ) : (
                      direct.privacy === 3 && (
                        <Tooltip title={"Direct archivé"}>
                          <LockIcon width={"1rem"} height={"1rem"} fill={theme.palette.soft.main} />
                        </Tooltip>
                      )
                    ))}
                </Box>
              )}
            </Box>
            {direct.date && (
              <Typography noWrap variant="subtitle1" fontSize={".8rem"} color={"gray"} textOverflow="ellipsis">
                {FormatDate(direct.date, true)}
              </Typography>
            )}
          </Box>
        </HoverOverlay>
      </StylelessLink>
    </Box>
  )
}

interface DirectPosterImageProps {
  direct: DirectType | null
}

export const DirectPosterImage = ({ direct }: DirectPosterImageProps): React.ReactElement => {
  const [posterSource, setPosterSource] = useState<string | null>(null)

  useEffect(() => {
    if (!direct) return
    const fetchPoster = async () => {
      try {
        const response = await fetch(`/api/media/poster/${direct.id}`, {
          credentials: "same-origin",
        })

        if (response.status === 200) {
          const blob = await response.blob()

          const posterURL = URL.createObjectURL(blob)
          setPosterSource(posterURL)
        } else {
          setPosterSource(null)
        }
      } catch (error) {
        console.error("Error fetching poster :", error)
        setPosterSource(null)
      }
    }

    if (direct) fetchPoster()
  }, [direct])

  return direct && posterSource ? (
    <DirectCardMedia image={posterSource || undefined} title={direct.title} className="adaptiveBorderRadius" />
  ) : (
    <img src={DefaultPoster} className="adaptiveBorderRadius" />
  )
}
